import type { Key, KeyColor, SymbolItem } from '@/types/piclick';
import { LETTER_SYMBOL_ID, isKeyComplete } from '@/lib/customization';

type Props = {
  index: number;
  keyData: Key;
  symbols: SymbolItem[];
  keyColors: KeyColor[];
  isOpen: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<Key>) => void;
};

export function KeyAccordion({
  index,
  keyData,
  symbols,
  keyColors,
  isOpen,
  onToggle,
  onChange,
}: Props) {
  const color = keyColors.find((c) => c.id === keyData.colorId);
  const symbol = symbols.find((s) => s.id === keyData.symbolId);
  const complete = isKeyComplete(keyData);

  return (
    <li
      id={`key-${index}`}
      tabIndex={-1}
      className={`overflow-hidden rounded-[1.5rem] border-2 transition-colors ${
        isOpen ? 'border-plum' : 'border-plum/15'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`key-${index}-content`}
        className="flex w-full items-center gap-3 bg-white px-4 py-3 text-left active:bg-cream/60"
      >
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[0.75rem] border-2"
          style={{
            backgroundColor: color?.hex ?? '#fffdf5',
            borderColor: color ? 'rgba(96,11,46,0.3)' : 'rgba(96,11,46,0.2)',
          }}
        >
          {symbol?.id === LETTER_SYMBOL_ID && keyData.letterValue ? (
            <span className="font-action text-base font-bold text-plum">
              {keyData.letterValue}
            </span>
          ) : symbol?.iconUrl ? (
            <img src={symbol.iconUrl} alt="" className="h-5 w-5" />
          ) : (
            <span className="text-xs font-bold text-plum/40">{index + 1}</span>
          )}
        </span>

        <span className="flex-1">
          <span className="block font-action text-sm font-bold text-plum">
            Tecla {index + 1}
          </span>
          <span className="block text-xs text-plum/60">
            {complete ? 'Tocar para editar' : 'Configurar'}
          </span>
        </span>

        <span
          aria-hidden="true"
          className={`text-plum/60 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          ▾
        </span>
      </button>

      {isOpen && (
        <div
          id={`key-${index}-content`}
          className="space-y-5 border-t-2 border-plum/15 bg-cream/40 p-4"
        >
          <div>
            <h5 className="mb-2 font-action text-xs font-bold uppercase tracking-wide text-plum/70">
              Letra ou Ícone
            </h5>
            <ul className="grid grid-cols-3 gap-2">
              {symbols.map((s) => {
                const isSelected = keyData.symbolId === s.id;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => onChange({ symbolId: s.id })}
                      aria-pressed={isSelected}
                      className={`flex w-full flex-col items-center gap-1 rounded-[1rem] border-2 p-2 transition-colors ${
                        isSelected
                          ? 'border-plum bg-butter'
                          : 'border-plum/20 bg-white hover:border-plum/50'
                      }`}
                    >
                      <span className="flex h-9 w-9 items-center justify-center">
                        {s.id === LETTER_SYMBOL_ID ? (
                          <span className="font-action text-2xl font-bold text-plum">A</span>
                        ) : s.iconUrl ? (
                          <img src={s.iconUrl} alt="" className="h-7 w-7" />
                        ) : (
                          <span className="text-plum/40">?</span>
                        )}
                      </span>
                      <span className="text-[10px] font-medium leading-none text-plum/80">
                        {s.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {keyData.symbolId === LETTER_SYMBOL_ID && (
              <div className="mt-3">
                <label
                  htmlFor={`letter-${index}`}
                  className="mb-1 block font-action text-xs font-bold uppercase tracking-wide text-plum/70"
                >
                  Digite a letra
                </label>
                <input
                  id={`letter-${index}`}
                  type="text"
                  maxLength={1}
                  autoCapitalize="characters"
                  autoComplete="off"
                  inputMode="text"
                  placeholder="A"
                  value={keyData.letterValue}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
                    onChange({ letterValue: v });
                  }}
                  className="w-20 rounded-xl border-2 border-plum/30 bg-white px-3 py-2 text-center font-action text-2xl font-bold uppercase text-plum focus:border-plum focus:outline-none"
                />
              </div>
            )}
          </div>

          <div>
            <h5 className="mb-2 font-action text-xs font-bold uppercase tracking-wide text-plum/70">
              Cor da Tecla
            </h5>
            <ul className="grid grid-cols-6 gap-2.5">
              {keyColors.map((c) => {
                const isSelected = keyData.colorId === c.id;
                return (
                  <li key={c.id} className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => onChange({ colorId: c.id })}
                      aria-pressed={isSelected}
                      aria-label={c.label}
                      title={c.label}
                      className={`block h-10 w-10 rounded-full border-[3px] transition-all ${
                        isSelected
                          ? 'border-plum ring-2 ring-plum ring-offset-1'
                          : 'border-plum/30'
                      }`}
                      style={{ backgroundColor: c.hex }}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </li>
  );
}
